import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import {
  listInvoiceReport,
  type InvoiceReportRow,
  type InvoiceReportSummary,
  type InvoiceReportModule,
  type InvoiceReportStatus,
  type InvoiceReportSubStage,
  type InvoiceReportSortBy,
  type InvoiceReportSortOrder,
} from "api/reports";

import iconSearch from "../../assets/svg/icon-search.svg";
import iconFilter from "../../assets/svg/icon-filter.svg";
import iconFilterSort from "../../assets/svg/icon-filter-sort.svg";

const PAGE_SIZE = 14;

const MODULE_OPTIONS: { value: InvoiceReportModule; label: string }[] = [
  { value: "NEW_APP", label: "New Application" },
  { value: "FOLLOW_UP", label: "Follow Up" },
  { value: "OPPOSITION", label: "Opposition" },
  { value: "ASSIGNMENT", label: "Assignment" },
  { value: "RENEWAL", label: "Renewal" },
  { value: "LEGAL_CERTIFICATE", label: "Legal Certificate" },
  { value: "SURVEILLANCE", label: "Surveillance" },
];

const STATUS_OPTIONS: { value: InvoiceReportStatus; label: string; tone: string }[] = [
  { value: "PAID", label: "Paid", tone: "bg-[#E6F4EA] text-[#1E7E34]" },
  { value: "PARTIALLY_PAID", label: "Partially Paid", tone: "bg-[#FFF4E5] text-[#B26A00]" },
  { value: "UNPAID", label: "Unpaid", tone: "bg-[#F7F7F7] text-[#555555]" },
  { value: "OVERDUE", label: "Overdue", tone: "bg-[#FDECEA] text-[#C0392B]" },
];

const SORT_OPTIONS: {
  value: string;
  label: string;
  sortBy: InvoiceReportSortBy;
  sortOrder: InvoiceReportSortOrder;
}[] = [
  { value: "invoiceDate-DESC", label: "Invoice date (newest first)", sortBy: "invoiceDate", sortOrder: "DESC" },
  { value: "invoiceDate-ASC", label: "Invoice date (oldest first)", sortBy: "invoiceDate", sortOrder: "ASC" },
  { value: "dueDate-ASC", label: "Due date (earliest first)", sortBy: "dueDate", sortOrder: "ASC" },
  { value: "dueDate-DESC", label: "Due date (latest first)", sortBy: "dueDate", sortOrder: "DESC" },
  { value: "totalAmount-DESC", label: "Total amount (high to low)", sortBy: "totalAmount", sortOrder: "DESC" },
  { value: "totalAmount-ASC", label: "Total amount (low to high)", sortBy: "totalAmount", sortOrder: "ASC" },
  { value: "outstandingAmount-DESC", label: "Outstanding (high to low)", sortBy: "outstandingAmount", sortOrder: "DESC" },
  { value: "collectedAmount-DESC", label: "Collected (high to low)", sortBy: "collectedAmount", sortOrder: "DESC" },
];

const MODULE_LABEL: Record<InvoiceReportModule, string> = MODULE_OPTIONS.reduce(
  (acc, m) => ({ ...acc, [m.value]: m.label }),
  {} as Record<InvoiceReportModule, string>
);

function formatCurrency(n: number): string {
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatSubStage(s: string): string {
  return s
    .toLowerCase()
    .split("_")
    .map((p) => (p.length ? p[0].toUpperCase() + p.slice(1) : p))
    .join(" ");
}

function statusTone(status: InvoiceReportStatus): string {
  return STATUS_OPTIONS.find((o) => o.value === status)?.tone ?? "bg-[#F7F7F7] text-[#555555]";
}

function statusLabel(status: InvoiceReportStatus): string {
  return STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

/**
 * Resolves the in-app path for an invoice row so a row click lands on the
 * collected-amount/payment section for that specific sub-stage. NEW_APP stage
 * invoices (Hearing, FC Reply, Counter, Evidence, etc.) deep-link into the
 * relevant Status page using its `?sub=…&substep=…` query params.
 */
function getInvoiceTargetPath(
  module: InvoiceReportModule,
  subStage: InvoiceReportSubStage,
  leadId: string
): string {
  const newAppBase = `/dashboard/applications/new-application`;

  switch (module) {
    case "NEW_APP": {
      switch (subStage) {
        case "NEW_FILING":
          return `${newAppBase}/discussion/payment-from-client/${leadId}`;
        case "EXPEDITED":
          return `${newAppBase}/filing/payment-from-client-expedited/${leadId}`;
        case "FC_REPLY":
          return `${newAppBase}/status/formalities-check/${leadId}?status=fail&substep=invoice-for-fc-failure`;
        case "REPLY_TO_OBJECTION":
          return `${newAppBase}/status/examination-report/${leadId}?sub=objected&substep=payment`;
        case "HEARING":
        case "HEARING_REVISED":
          return `${newAppBase}/status/examination-report/${leadId}?sub=hearing-after-objection&substep=payment-from-client-for-hearing`;
        case "TM_M_POSTPONE":
          return `${newAppBase}/status/examination-report/${leadId}?sub=hearing-after-objection&substep=payment-from-client-for-tmm-before-hearing`;
        case "TM_M_AFTER_HEARING":
          return `${newAppBase}/status/examination-report/${leadId}?sub=hearing-after-objection&substep=payment-from-client-for-tmm-after-hearing`;
        case "SUBSEQUENT_HEARING_2":
        case "SUBSEQUENT_HEARING_3":
        case "SUBSEQUENT_HEARING_4":
          return `${newAppBase}/status/examination-report/${leadId}?sub=hearing-after-objection&substep=subsequent-hearings`;
        case "COUNTER":
          return `${newAppBase}/status/advertised-in-journal/${leadId}?variant=opposed&sub=payment-from-client-counter`;
        case "EVIDENCE":
          return `${newAppBase}/status/advertised-in-journal/${leadId}?variant=opposed&sub=payment-evidence`;
        case "OPPOSITION_HEARING":
          return `${newAppBase}/status/advertised-in-journal/${leadId}?variant=opposed&sub=payment-opposition-hearing`;
        case "TM_M_BEFORE_OPPOSITION_HEARING":
          return `${newAppBase}/status/advertised-in-journal/${leadId}?variant=opposed&sub=payment-tmm-before-opp-hearing`;
        case "TM_M_AFTER_OPPOSITION_HEARING":
          return `${newAppBase}/status/advertised-in-journal/${leadId}?variant=opposed&sub=invoice-tmm-after-opp-hearing`;
        case "SUBSEQUENT_OPPOSITION_HEARING_2":
        case "SUBSEQUENT_OPPOSITION_HEARING_3":
        case "SUBSEQUENT_OPPOSITION_HEARING_4":
          return `${newAppBase}/status/advertised-in-journal/${leadId}?variant=opposed&sub=subsequent-opp-hearings`;
        default:
          return `${newAppBase}/discussion/payment-from-client/${leadId}`;
      }
    }
    case "FOLLOW_UP":
      return `/dashboard/follow-ups/new/discussion/payment-from-client/${leadId}`;
    case "OPPOSITION":
      if (subStage === "OPPOSITION_EVIDENCE") {
        return `/dashboard/opposition/new/status/payment-from-client-for-evidence-by-opponent/${leadId}`;
      }
      return `/dashboard/opposition/new/discussion/payment-from-client/${leadId}`;
    case "ASSIGNMENT":
      return `/dashboard/assignments/new/discussion/payment-from-client/${leadId}`;
    case "RENEWAL":
      return `/dashboard/renewal/new/discussion/payment-from-client-for-renewal/${leadId}`;
    case "LEGAL_CERTIFICATE":
      return `/dashboard/legal-certificate/new/discussion/payment-from-client-for-legal-certificate/${leadId}`;
    case "SURVEILLANCE":
      return `/dashboard/surveillance/new/discussion/payment-from-client/${leadId}`;
    default:
      return `${newAppBase}/discussion/payment-from-client/${leadId}`;
  }
}

export default function InvoiceReportView() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [moduleFilter, setModuleFilter] = useState<Set<InvoiceReportModule>>(new Set());
  const [statusFilter, setStatusFilter] = useState<Set<InvoiceReportStatus>>(new Set());
  const [invoiceDateFrom, setInvoiceDateFrom] = useState("");
  const [invoiceDateTo, setInvoiceDateTo] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");

  const [sortValue, setSortValue] = useState<string>("invoiceDate-DESC");

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<InvoiceReportRow[]>([]);
  const [summary, setSummary] = useState<InvoiceReportSummary>({
    totalInvoiceAmount: 0,
    totalCollectedAmount: 0,
    totalOutstandingAmount: 0,
    invoiceCount: 0,
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [moduleOpen, setModuleOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [amountOpen, setAmountOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const moduleRef = useRef<HTMLDivElement | null>(null);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const dateRef = useRef<HTMLDivElement | null>(null);
  const amountRef = useRef<HTMLDivElement | null>(null);
  const sortRef = useRef<HTMLDivElement | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const activeSort = useMemo(
    () => SORT_OPTIONS.find((o) => o.value === sortValue) ?? SORT_OPTIONS[0],
    [sortValue]
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    moduleFilter,
    statusFilter,
    invoiceDateFrom,
    invoiceDateTo,
    amountMin,
    amountMax,
    sortValue,
  ]);

  useEffect(() => {
    if (!moduleOpen && !statusOpen && !dateOpen && !amountOpen && !sortOpen) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (moduleOpen && moduleRef.current && !moduleRef.current.contains(t)) setModuleOpen(false);
      if (statusOpen && statusRef.current && !statusRef.current.contains(t)) setStatusOpen(false);
      if (dateOpen && dateRef.current && !dateRef.current.contains(t)) setDateOpen(false);
      if (amountOpen && amountRef.current && !amountRef.current.contains(t)) setAmountOpen(false);
      if (sortOpen && sortRef.current && !sortRef.current.contains(t)) setSortOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [moduleOpen, statusOpen, dateOpen, amountOpen, sortOpen]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const totalMin = amountMin.trim() === "" ? undefined : Number(amountMin);
      const totalMax = amountMax.trim() === "" ? undefined : Number(amountMax);
      const data = await listInvoiceReport({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch,
        modules: moduleFilter.size > 0 ? Array.from(moduleFilter) : undefined,
        statuses: statusFilter.size > 0 ? Array.from(statusFilter) : undefined,
        invoiceDateFrom: invoiceDateFrom || undefined,
        invoiceDateTo: invoiceDateTo || undefined,
        totalMin: Number.isFinite(totalMin) ? totalMin : undefined,
        totalMax: Number.isFinite(totalMax) ? totalMax : undefined,
        sortBy: activeSort.sortBy,
        sortOrder: activeSort.sortOrder,
      });
      setRows(data.rows);
      setSummary(data.summary);
      setTotal(data.total);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load invoice report");
      setRows([]);
      setSummary({
        totalInvoiceAmount: 0,
        totalCollectedAmount: 0,
        totalOutstandingAmount: 0,
        invoiceCount: 0,
      });
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    debouncedSearch,
    moduleFilter,
    statusFilter,
    invoiceDateFrom,
    invoiceDateTo,
    amountMin,
    amountMax,
    activeSort,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const toggleModule = (m: InvoiceReportModule) => {
    setModuleFilter((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });
  };

  const toggleStatus = (s: InvoiceReportStatus) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const clearAllFilters = () => {
    setModuleFilter(new Set());
    setStatusFilter(new Set());
    setInvoiceDateFrom("");
    setInvoiceDateTo("");
    setAmountMin("");
    setAmountMax("");
    setSearchQuery("");
  };

  const hasActiveFilters =
    moduleFilter.size > 0 ||
    statusFilter.size > 0 ||
    invoiceDateFrom !== "" ||
    invoiceDateTo !== "" ||
    amountMin !== "" ||
    amountMax !== "" ||
    debouncedSearch.trim() !== "";

  return (
    <DashboardLayout headerBrand="UpRight" headerTitle="Invoice Report">
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2">
        <div className="flex min-w-0 flex-col gap-5 rounded-[8px] bg-white p-5 sm:h-full">
          <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <h1 className="text-[20px] font-semibold leading-5 text-[#222222]">Invoice Report</h1>
            <div className="flex w-full min-w-0 flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              <div className="flex h-9 w-full flex-row items-center gap-2 rounded-[6px] bg-[#F7F7F7] px-3 py-2 sm:w-[250px]">
                <img src={iconSearch} alt="" className="w-3.5 h-3.5 shrink-0" />
                <input
                  type="text"
                  placeholder="Search lead / client / invoice no"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 min-w-0 bg-[#F7F7F7] text-xs font-normal leading-5 text-[#222222] placeholder:text-[#777777] focus:outline-none"
                />
              </div>

              {/* Module filter */}
              <div ref={moduleRef} className="relative w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => {
                    setModuleOpen((v) => !v);
                    setStatusOpen(false);
                    setDateOpen(false);
                    setAmountOpen(false);
                    setSortOpen(false);
                  }}
                  className={`flex h-9 w-full items-center justify-between gap-2 rounded-[6px] border px-3 py-2 transition-colors sm:w-auto sm:justify-start ${
                    moduleFilter.size > 0
                      ? "border-[#222222] bg-[#F7F7F7]"
                      : "border-[#EEEEEE] bg-white hover:bg-[#F7F7F7]"
                  }`}
                >
                  <img src={iconFilter} alt="" className="w-3.5 h-3.5" />
                  <span className="text-xs font-normal leading-5 text-[#222222]">
                    {moduleFilter.size === 0
                      ? "Module"
                      : `Module (${moduleFilter.size})`}
                  </span>
                </button>
                {moduleOpen && (
                  <div className="absolute left-0 right-0 mt-1 w-auto bg-white border border-[#EEEEEE] rounded-[6px] shadow-md py-1 z-30 max-h-[260px] overflow-auto sm:left-auto sm:w-56">
                    {MODULE_OPTIONS.map((opt) => {
                      const selected = moduleFilter.has(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleModule(opt.value)}
                          className={`flex items-center justify-between w-full px-3 py-2 text-left text-xs leading-5 transition-colors ${
                            selected
                              ? "bg-[#F7F7F7] text-[#222222] font-medium"
                              : "text-[#444444] hover:bg-[#FAFAFA]"
                          }`}
                        >
                          {opt.label}
                          {selected && (
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M3.5 8.5L6.5 11.5L12.5 5"
                                stroke="#222222"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                    {moduleFilter.size > 0 && (
                      <div className="border-t border-[#EEEEEE] mt-1 pt-1">
                        <button
                          type="button"
                          onClick={() => setModuleFilter(new Set())}
                          className="w-full px-3 py-2 text-left text-xs text-[#777777] hover:bg-[#FAFAFA]"
                        >
                          Clear modules
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Status filter */}
              <div ref={statusRef} className="relative w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => {
                    setStatusOpen((v) => !v);
                    setModuleOpen(false);
                    setDateOpen(false);
                    setAmountOpen(false);
                    setSortOpen(false);
                  }}
                  className={`flex h-9 w-full items-center justify-between gap-2 rounded-[6px] border px-3 py-2 transition-colors sm:w-auto sm:justify-start ${
                    statusFilter.size > 0
                      ? "border-[#222222] bg-[#F7F7F7]"
                      : "border-[#EEEEEE] bg-white hover:bg-[#F7F7F7]"
                  }`}
                >
                  <img src={iconFilter} alt="" className="w-3.5 h-3.5" />
                  <span className="text-xs font-normal leading-5 text-[#222222]">
                    {statusFilter.size === 0
                      ? "Status"
                      : `Status (${statusFilter.size})`}
                  </span>
                </button>
                {statusOpen && (
                  <div className="absolute left-0 right-0 mt-1 w-auto bg-white border border-[#EEEEEE] rounded-[6px] shadow-md py-1 z-30 sm:left-auto sm:w-48">
                    {STATUS_OPTIONS.map((opt) => {
                      const selected = statusFilter.has(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleStatus(opt.value)}
                          className={`flex items-center justify-between w-full px-3 py-2 text-left text-xs leading-5 transition-colors ${
                            selected
                              ? "bg-[#F7F7F7] text-[#222222] font-medium"
                              : "text-[#444444] hover:bg-[#FAFAFA]"
                          }`}
                        >
                          {opt.label}
                          {selected && (
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M3.5 8.5L6.5 11.5L12.5 5"
                                stroke="#222222"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Date range filter */}
              <div ref={dateRef} className="relative w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => {
                    setDateOpen((v) => !v);
                    setModuleOpen(false);
                    setStatusOpen(false);
                    setAmountOpen(false);
                    setSortOpen(false);
                  }}
                  className={`flex h-9 w-full items-center justify-between gap-2 rounded-[6px] border px-3 py-2 transition-colors sm:w-auto sm:justify-start ${
                    invoiceDateFrom || invoiceDateTo
                      ? "border-[#222222] bg-[#F7F7F7]"
                      : "border-[#EEEEEE] bg-white hover:bg-[#F7F7F7]"
                  }`}
                >
                  <img src={iconFilter} alt="" className="w-3.5 h-3.5" />
                  <span className="text-xs font-normal leading-5 text-[#222222]">
                    {invoiceDateFrom || invoiceDateTo ? "Date set" : "Date"}
                  </span>
                </button>
                {dateOpen && (
                  <div className="absolute left-0 right-0 mt-1 w-auto bg-white border border-[#EEEEEE] rounded-[6px] shadow-md p-3 z-30 sm:left-auto sm:w-64">
                    <label className="block text-[11px] font-medium text-[#444444] mb-1">From</label>
                    <input
                      type="date"
                      value={invoiceDateFrom}
                      onChange={(e) => setInvoiceDateFrom(e.target.value)}
                      className="w-full h-9 px-2 mb-3 border border-[#EEEEEE] rounded-[6px] text-xs text-[#222222] focus:outline-none focus:ring-1 focus:ring-[#222]"
                    />
                    <label className="block text-[11px] font-medium text-[#444444] mb-1">To</label>
                    <input
                      type="date"
                      value={invoiceDateTo}
                      onChange={(e) => setInvoiceDateTo(e.target.value)}
                      className="w-full h-9 px-2 mb-3 border border-[#EEEEEE] rounded-[6px] text-xs text-[#222222] focus:outline-none focus:ring-1 focus:ring-[#222]"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setInvoiceDateFrom("");
                          setInvoiceDateTo("");
                        }}
                        className="h-8 px-3 rounded-[6px] border border-[#EEEEEE] bg-white text-xs text-[#6D6D6D] hover:bg-[#F7F7F7]"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={() => setDateOpen(false)}
                        className="h-8 px-3 rounded-[6px] bg-[#222222] text-xs font-semibold text-white hover:bg-[#333333]"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Amount range filter */}
              <div ref={amountRef} className="relative w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => {
                    setAmountOpen((v) => !v);
                    setModuleOpen(false);
                    setStatusOpen(false);
                    setDateOpen(false);
                    setSortOpen(false);
                  }}
                  className={`flex h-9 w-full items-center justify-between gap-2 rounded-[6px] border px-3 py-2 transition-colors sm:w-auto sm:justify-start ${
                    amountMin !== "" || amountMax !== ""
                      ? "border-[#222222] bg-[#F7F7F7]"
                      : "border-[#EEEEEE] bg-white hover:bg-[#F7F7F7]"
                  }`}
                >
                  <img src={iconFilter} alt="" className="w-3.5 h-3.5" />
                  <span className="text-xs font-normal leading-5 text-[#222222]">
                    {amountMin !== "" || amountMax !== "" ? "Amount set" : "Amount"}
                  </span>
                </button>
                {amountOpen && (
                  <div className="absolute left-0 right-0 mt-1 w-auto bg-white border border-[#EEEEEE] rounded-[6px] shadow-md p-3 z-30 sm:left-auto sm:w-64">
                    <label className="block text-[11px] font-medium text-[#444444] mb-1">
                      Min total
                    </label>
                    <input
                      type="number"
                      min="0"
                      inputMode="decimal"
                      value={amountMin}
                      onChange={(e) => setAmountMin(e.target.value)}
                      placeholder="0"
                      className="w-full h-9 px-2 mb-3 border border-[#EEEEEE] rounded-[6px] text-xs text-[#222222] focus:outline-none focus:ring-1 focus:ring-[#222]"
                    />
                    <label className="block text-[11px] font-medium text-[#444444] mb-1">
                      Max total
                    </label>
                    <input
                      type="number"
                      min="0"
                      inputMode="decimal"
                      value={amountMax}
                      onChange={(e) => setAmountMax(e.target.value)}
                      placeholder="No limit"
                      className="w-full h-9 px-2 mb-3 border border-[#EEEEEE] rounded-[6px] text-xs text-[#222222] focus:outline-none focus:ring-1 focus:ring-[#222]"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setAmountMin("");
                          setAmountMax("");
                        }}
                        className="h-8 px-3 rounded-[6px] border border-[#EEEEEE] bg-white text-xs text-[#6D6D6D] hover:bg-[#F7F7F7]"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={() => setAmountOpen(false)}
                        className="h-8 px-3 rounded-[6px] bg-[#222222] text-xs font-semibold text-white hover:bg-[#333333]"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Sort */}
              <div ref={sortRef} className="relative w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => {
                    setSortOpen((v) => !v);
                    setModuleOpen(false);
                    setStatusOpen(false);
                    setDateOpen(false);
                    setAmountOpen(false);
                  }}
                  className={`flex h-9 w-full max-w-full items-center justify-between gap-2 rounded-[6px] border px-3 py-2 transition-colors sm:w-auto sm:max-w-[240px] sm:justify-start ${
                    sortValue !== "invoiceDate-DESC"
                      ? "border-[#222222] bg-[#F7F7F7]"
                      : "border-[#EEEEEE] bg-white hover:bg-[#F7F7F7]"
                  }`}
                >
                  <img src={iconFilterSort} alt="" className="w-3.5 h-3.5" />
                  <span className="min-w-0 truncate text-xs font-normal leading-5 text-[#222222]">
                    {activeSort.label}
                  </span>
                </button>
                {sortOpen && (
                  <div className="absolute left-0 right-0 mt-1 w-auto bg-white border border-[#EEEEEE] rounded-[6px] shadow-md py-1 z-30 sm:left-auto sm:w-64">
                    {SORT_OPTIONS.map((opt) => {
                      const selected = sortValue === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            setSortValue(opt.value);
                            setSortOpen(false);
                          }}
                          className={`flex items-center justify-between w-full px-3 py-2 text-left text-xs leading-5 transition-colors ${
                            selected
                              ? "bg-[#F7F7F7] text-[#222222] font-medium"
                              : "text-[#444444] hover:bg-[#FAFAFA]"
                          }`}
                        >
                          {opt.label}
                          {selected && (
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M3.5 8.5L6.5 11.5L12.5 5"
                                stroke="#222222"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="flex items-center px-3 h-9 rounded-[6px] text-xs font-medium text-[#C0392B] hover:bg-[#FDECEA] transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard label="Total invoices" value={String(summary.invoiceCount)} accent="#222222" />
            <SummaryCard
              label="Total invoiced"
              value={formatCurrency(summary.totalInvoiceAmount)}
              accent="#222222"
            />
            <SummaryCard
              label="Collected"
              value={formatCurrency(summary.totalCollectedAmount)}
              accent="#1E7E34"
            />
            <SummaryCard
              label="Outstanding"
              value={formatCurrency(summary.totalOutstandingAmount)}
              accent="#C0392B"
            />
          </div>

          {/* Table */}
          <div className="flex min-w-0 flex-col overflow-hidden rounded-[8px] border border-[#EEEEEE] sm:min-h-0 sm:flex-1">
            <div className="min-w-0 overflow-x-auto sm:min-h-0 sm:flex-1 sm:overflow-auto">
              <table className="w-full min-w-[1280px] table-fixed border-collapse">
                <colgroup>
                  <col className="w-[130px]" />
                  <col className="w-[150px]" />
                  <col className="w-[130px]" />
                  <col className="w-[150px]" />
                  <col className="w-[170px]" />
                  <col className="w-[120px]" />
                  <col className="w-[100px]" />
                  <col className="w-[100px]" />
                  <col className="w-[120px]" />
                  <col className="w-[110px]" />
                </colgroup>
                <thead>
                  <tr className="bg-[#F7F7F7] border-b border-[#EEEEEE]">
                    <Th>Invoice No</Th>
                    <Th>Lead</Th>
                    <Th>Module</Th>
                    <Th>Type</Th>
                    <Th>Client</Th>
                    <Th>Invoice Date</Th>
                    <Th align="right">Total</Th>
                    <Th align="right">Collected</Th>
                    <Th align="right">Outstanding</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0F0F0]">
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="px-5 py-16 text-center text-sm text-[#777777]">
                        Loading invoice report…
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-5 py-16 text-center text-sm text-[#777777]">
                        No invoices match the current filters.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr
                        key={`${row.invoiceId}-${row.subStage}`}
                        onClick={() => {
                          if (!row.leadId) return;
                          navigate(getInvoiceTargetPath(row.module, row.subStage, row.leadId));
                        }}
                        className="hover:bg-[#FAFAFA] transition-colors cursor-pointer"
                      >
                        <Td title={row.invoiceNo ?? undefined}>
                          {row.invoiceNo || <Empty />}
                        </Td>
                        <Td title={row.leadNo ?? undefined}>
                          {row.leadNo ? (
                            <div className="flex flex-col">
                              <span className="font-medium text-[#222222]">{row.leadNo}</span>
                              {row.markName && (
                                <span className="text-[11px] text-[#777777] truncate">
                                  {row.markName}
                                </span>
                              )}
                            </div>
                          ) : (
                            <Empty />
                          )}
                        </Td>
                        <Td>{MODULE_LABEL[row.module] ?? row.module}</Td>
                        <Td title={formatSubStage(row.subStage)}>{formatSubStage(row.subStage)}</Td>
                        <Td title={row.clientName ?? undefined}>{row.clientName || <Empty />}</Td>
                        <Td>{row.invoiceDate || <Empty />}</Td>
                        <Td align="right" className="tabular-nums font-medium text-[#222222]">
                          {formatCurrency(row.totalAmount)}
                        </Td>
                        <Td align="right" className="tabular-nums text-[#1E7E34]">
                          {formatCurrency(row.collectedAmount)}
                        </Td>
                        <Td align="right" className="tabular-nums text-[#C0392B]">
                          {formatCurrency(row.outstandingAmount)}
                        </Td>
                        <Td>
                          <span
                            className={`inline-flex items-center h-6 px-2 rounded-full text-[11px] font-medium ${statusTone(
                              row.status
                            )}`}
                          >
                            {statusLabel(row.status)}
                          </span>
                        </Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-row justify-end items-center gap-6 shrink-0">
            <span className="text-xs font-normal leading-5 text-[#6D6D6D]">
              {total === 0
                ? "No records"
                : `Showing ${(page - 1) * PAGE_SIZE + 1} to ${Math.min(
                    page * PAGE_SIZE,
                    total
                  )} of ${total} records`}
            </span>
            <div className="flex flex-row items-center gap-3">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex items-center justify-center w-7 h-7 border border-[#EEEEEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="rotate-180">
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="#222222"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="flex items-center justify-center w-7 h-7 border border-[#EEEEEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="#222222"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function SummaryCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: string;
}) {
  return (
    <div className="border border-[#EEEEEE] rounded-[8px] p-4 bg-white">
      <div className="text-[11px] font-medium uppercase tracking-wider text-[#6D6D6D]">{label}</div>
      <div
        className="mt-2 text-[20px] font-semibold leading-7 tabular-nums truncate"
        style={{ color: accent }}
        title={value}
      >
        {value}
      </div>
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={`sticky top-0 z-10 bg-[#F7F7F7] px-5 h-11 text-[11px] font-semibold uppercase tracking-wider leading-4 text-[#6D6D6D] ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  className = "",
  title,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  className?: string;
  title?: string;
}) {
  return (
    <td
      className={`px-5 py-3 align-middle text-xs leading-5 text-[#222222] truncate ${
        align === "right" ? "text-right" : "text-left"
      } ${className}`}
      title={title}
    >
      {children}
    </td>
  );
}

function Empty() {
  return <span className="text-[#999999]">–</span>;
}
